import { cx } from '../lib/cx';
import { handleRadioGroupKey } from '../lib/radioGroupKeys';

export interface SegmentedOption<T extends string> {
  value: T;
  label: string;
}

export interface SegmentedControlProps<T extends string> {
  ariaLabel: string;
  value: T;
  options: readonly SegmentedOption<T>[];
  onChange: (value: T) => void;
  className?: string;
}

/**
 * Filter u jednom redu iznad grafa. Radiogroup, ne skup gumba — čitač zaslona
 * mora znati da je odabir jedan od nekoliko, a ne pet nepovezanih akcija.
 */
export function SegmentedControl<T extends string>({
  ariaLabel,
  value,
  options,
  onChange,
  className,
}: SegmentedControlProps<T>) {
  const hasSelection = options.some((o) => o.value === value);
  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      className={cx(
        'inline-flex max-w-full gap-1 overflow-x-auto rounded-pill border border-line-subtle bg-bg-alt p-1',
        className,
      )}
    >
      {options.map((option, index) => {
        const selected = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={selected}
            /* Skupina je jedna postaja tabulatora; unutra se ide strelicama. */
            tabIndex={selected || (!hasSelection && index === 0) ? 0 : -1}
            onKeyDown={(e) =>
              handleRadioGroupKey(
                e,
                index,
                options.map((o) => o.value),
                onChange,
              )
            }
            onClick={() => onChange(option.value)}
            className={cx(
              'shrink-0 whitespace-nowrap rounded-pill px-3.5 py-1.5 text-[13px] transition-colors duration-200',
              selected ? 'bg-surface text-ink shadow-card' : 'text-ink-muted hover:text-ink',
            )}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}
